// Team-Chat. Das Backend hat keine Live-Verbindung (kein WebSocket), deshalb
// wird im Hintergrund nachgefragt. Das Intervall ist bewusst moderat: die alte
// App hat sehr häufig gepollt und damit den Server unnötig belastet.
//
// Die Empfängerwahl lag früher in einem Auswahlfeld im Kopf. Jetzt steht links
// eine Liste aller Gesprächspartner mit Online-Punkt, letzter Nachricht und
// Zähler für Ungelesenes — man sieht auf einen Blick, wer geschrieben hat,
// statt ein Klappmenü durchzugehen.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { anhangAlsBlobUrl, anhangOeffnen, dateiHochladen, groesseLesbar, istBild, type Anhang } from '../../firma/lib/dateien';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useBenachrichtigungen } from '../lib/benachrichtigungen';
import type { Anwesenheit, Nachricht } from '../../../gemeinsam/lib/typen';
import { Hinweis, Karte, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import Icon from '../../../gemeinsam/komponenten/Icon';

const ABFRAGE_MS = 10000;

/** „Alle im Team" ist ein Gesprächspartner wie jeder andere. */
const TEAM = 'all';

export default function Chat() {
  const { t } = useSprache();
  const { benutzer } = useAuth();
  const { neuLaden: benachrichtigungenNeu } = useBenachrichtigungen();
  const [nachrichten, setNachrichten] = useState<Nachricht[] | null>(null);
  const [team, setTeam] = useState<Anwesenheit[]>([]);
  const [empfaenger, setEmpfaenger] = useState<string>(TEAM);
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const [anhang, setAnhang] = useState<Anhang | null>(null);
  const [laedtHoch, setLaedtHoch] = useState(false);
  // Auf schmalen Bildschirmen ist entweder die Liste oder das Gespräch sichtbar.
  const [listeOffen, setListeOffen] = useState(true);
  const endeRef = useRef<HTMLDivElement>(null);

  const laden = useCallback(async () => {
    try {
      const [n, t] = await Promise.all([
        api<Nachricht[]>('/messages'),
        api<Anwesenheit[]>('/presence/users').catch(() => [] as Anwesenheit[]),
      ]);
      setNachrichten(n);
      setTeam(t);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Nachrichten konnten nicht geladen werden.');
      setNachrichten([]);
    }
  }, []);

  useEffect(() => {
    void laden();
    const id = setInterval(() => { void laden(); }, ABFRAGE_MS);
    return () => clearInterval(id);
  }, [laden]);

  // Wer den Chat offen hat, hat die Nachrichten gesehen — also als gelesen
  // melden und den Zähler in der Navigation zurücksetzen.
  useEffect(() => {
    if (!nachrichten?.length || !benutzer?.id) return;
    const meineId = benutzer.id;
    const ungelesen = nachrichten.some(n => n.from !== meineId && !(n.readBy || []).includes(meineId));
    if (!ungelesen) return;

    void (async () => {
      try {
        await api('/messages/read-all', { methode: 'POST', daten: {} });
        setNachrichten(n => (n ?? []).map(m => ({
          ...m,
          readBy: m.readBy?.includes(meineId) ? m.readBy : [...(m.readBy ?? []), meineId],
        })));
        await benachrichtigungenNeu();
      } catch { /* beim nächsten Durchlauf erneut */ }
    })();
  }, [nachrichten, benutzer?.id, benachrichtigungenNeu]);

  const sichtbar = useMemo(() => (nachrichten ?? []).filter(n =>
    empfaenger === TEAM
      ? n.to === TEAM
      : (n.from === empfaenger && n.to === benutzer?.id) || (n.from === benutzer?.id && n.to === empfaenger)
  ), [nachrichten, empfaenger, benutzer?.id]);

  useEffect(() => {
    endeRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sichtbar.length, empfaenger]);

  /** Ein Eintrag der Gesprächsliste: letzte Nachricht und Ungelesenes. */
  const gespraeche = useMemo(() => {
    const alle = nachrichten ?? [];
    const meineId = benutzer?.id ?? '';

    const bauen = (id: string, name: string, online: boolean) => {
      const passend = alle.filter(n => id === TEAM
        ? n.to === TEAM
        : (n.from === id && n.to === meineId) || (n.from === meineId && n.to === id));
      const letzte = passend[passend.length - 1];
      const ungelesen = passend.filter(n => n.from !== meineId && !(n.readBy || []).includes(meineId)).length;
      return { id, name, online, letzte, ungelesen };
    };

    return [
      bauen(TEAM, 'Alle im Team', team.some(t => t.online && t.id !== meineId)),
      ...team
        .filter(t => t.id !== meineId)
        .map(t => bauen(t.id, t.name, t.online))
        // Wer ungelesene Nachrichten hat, steht oben; danach das jüngste
        // Gespräch — meist genau der, dem man antworten will.
        .sort((a, b) => {
          if (a.ungelesen !== b.ungelesen) return b.ungelesen - a.ungelesen;
          const za = a.letzte ? new Date(a.letzte.createdAt).getTime() : 0;
          const zb = b.letzte ? new Date(b.letzte.createdAt).getTime() : 0;
          return zb - za;
        }),
    ];
  }, [nachrichten, team, benutzer?.id]);

  const aktuell = gespraeche.find(g => g.id === empfaenger);

  // Der gewählte Anhang wartet neben dem Eingabefeld, bis die Nachricht
  // abgeschickt wird — so sieht man vorher, was mitgeht.
  async function dateiWaehlen(datei: File | null | undefined) {
    if (!datei) return;
    setFehler(null);
    setLaedtHoch(true);
    try {
      setAnhang(await dateiHochladen(datei));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Datei konnte nicht hochgeladen werden.');
    } finally { setLaedtHoch(false); }
  }

  async function senden(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() && !anhang) return;
    setSendet(true);
    try {
      const neu = await api<Nachricht>('/messages', {
        methode: 'POST',
        daten: { to: empfaenger, text: text.trim(), dateiId: anhang?.id },
      });
      setNachrichten(n => [...(n ?? []), neu]);
      setText('');
      setAnhang(null);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Nachricht konnte nicht gesendet werden.');
    } finally { setSendet(false); }
  }

  if (nachrichten === null) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] max-w-5xl flex-col gap-4">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('chat.titel')}</h1>
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[260px_1fr]">
        {/* Gesprächsliste */}
        <Karte klasse={`${listeOffen ? 'flex' : 'hidden'} min-h-0 flex-col lg:flex`}>
          <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('chat.wemSchreiben')}</p>
          </div>
          <ul className="min-h-0 flex-1 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-800">
            {gespraeche.map(g => {
              const gewaehlt = g.id === empfaenger;
              return (
                <li key={g.id}>
                  <button
                    onClick={() => { setEmpfaenger(g.id); setListeOffen(false); }}
                    className={`flex w-full items-center gap-2.5 px-4 py-3 text-start transition ${
                      gewaehlt
                        ? 'bg-marke-50 dark:bg-marke-950/50'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                  >
                    {g.id === TEAM ? (
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-marke-100 text-marke-700 dark:bg-marke-950 dark:text-marke-300">
                        <Icon name="team" groesse={16} />
                      </span>
                    ) : (
                      <span className="relative grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {g.name.charAt(0).toUpperCase()}
                        <span className={`absolute -bottom-0.5 -end-0.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-slate-900 ${
                          g.online ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                      </span>
                    )}

                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm ${
                        gewaehlt
                          ? 'font-semibold text-marke-700 dark:text-marke-300'
                          : 'font-medium text-slate-900 dark:text-slate-100'}`}>
                        {g.name}
                      </span>
                      <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                        {g.letzte
                          ? `${g.letzte.from === benutzer?.id ? 'Sie: ' : ''}${g.letzte.text}`
                          : g.id === TEAM ? 'Nachricht an alle' : g.online ? 'online' : 'offline'}
                      </span>
                    </span>

                    {g.ungelesen > 0 && (
                      <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white">
                        {g.ungelesen}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Karte>

        {/* Gespräch */}
        <Karte klasse={`${listeOffen ? 'hidden' : 'flex'} min-h-0 flex-col lg:flex`}>
          <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
            <button
              onClick={() => setListeOffen(true)}
              className="rounded-lg px-2 py-1 text-lg text-slate-500 hover:bg-slate-100 lg:hidden dark:hover:bg-slate-800"
              aria-label={t('chat.zurListe')}
            >‹</button>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-900 dark:text-slate-100">
                {aktuell?.name ?? 'Alle im Team'}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {empfaenger === TEAM
                  ? `${team.filter(t => t.online).length} von ${team.length} online`
                  : aktuell?.online ? 'online' : 'offline'}
              </p>
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
            {sichtbar.length === 0 ? (
              <LeerZustand
                titel={t('chat.leer')}
                text={empfaenger === TEAM
                  ? 'Schreiben Sie die erste Nachricht ans Team.'
                  : `Beginnen Sie das Gespräch mit ${aktuell?.name ?? 'dem Kollegen'}.`}
              />
            ) : sichtbar.map(n => {
              const eigen = n.from === benutzer?.id;
              const absender = team.find(t => t.id === n.from)?.name ?? 'Kollege';
              return (
                <div key={n.id} className={`flex ${eigen ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-xl px-3.5 py-2 ${eigen
                    ? 'bg-marke-600 text-white'
                    : 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100'}`}>
                    {/* Im Team-Chat muss der Absender dabeistehen, im
                        Einzelgespräch ist er offensichtlich. */}
                    {!eigen && empfaenger === TEAM && (
                      <p className="mb-0.5 text-xs font-medium opacity-70">{absender}</p>
                    )}
                    {n.text && <p className="whitespace-pre-wrap break-words text-sm">{n.text}</p>}
                    {n.anhang && <AnhangBlase anhang={n.anhang} eigen={eigen} melden={setFehler} />}
                    <p className={`mt-0.5 text-[10px] ${eigen ? 'text-white/60' : 'text-slate-500 dark:text-slate-400'}`}>
                      {new Date(n.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={endeRef} />
          </div>

          <div className="border-t border-slate-200 dark:border-slate-800">
            {anhang && (
              <div className="flex items-center gap-2 px-3 pt-3">
                <span className="flex min-w-0 items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-xs dark:bg-slate-800">
                  <span aria-hidden>📎</span>
                  <span className="truncate text-slate-700 dark:text-slate-200">{anhang.name}</span>
                  <span className="shrink-0 text-slate-400">{groesseLesbar(anhang.groesse)}</span>
                  <button type="button" onClick={() => setAnhang(null)}
                          aria-label={t('chat.anhangEntfernen')}
                          className="shrink-0 text-slate-400 hover:text-rose-500">✕</button>
                </span>
              </div>
            )}

            <form onSubmit={senden} className="flex gap-2 p-3">
              <label className={`grid h-9 w-9 shrink-0 cursor-pointer place-items-center rounded-lg border border-slate-300 text-slate-500 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 ${laedtHoch ? 'pointer-events-none opacity-50' : ''}`}
                     title="{t('chat.dateiAnhaengen')} (max. 25 MB)">
                <span aria-hidden>{laedtHoch ? '…' : '📎'}</span>
                <input type="file" className="hidden"
                       onChange={e => { void dateiWaehlen(e.target.files?.[0]); e.target.value = ''; }} />
                <span className="sr-only">{t('chat.dateiAnhaengen')}</span>
              </label>
              <input
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={empfaenger === TEAM ? 'Nachricht ans Team …' : `Nachricht an ${aktuell?.name ?? ''} …`}
                maxLength={5000}
                className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
              <Knopf type="submit" variante="primaer" laedt={sendet}
                     disabled={!text.trim() && !anhang}>{t('chat.senden')}</Knopf>
            </form>
          </div>
        </Karte>
      </div>
    </div>
  );
}

/**
 * Anhang in der Nachricht. Bilder bekommen eine Vorschau, alles andere eine
 * Zeile zum Herunterladen — Dateien werden nie im Seitenkontext geöffnet.
 */
function AnhangBlase({ anhang, eigen, melden }: {
  anhang: Anhang;
  eigen: boolean;
  melden: (nachricht: string | null) => void;
}) {
  const { t } = useSprache();

  async function holen() {
    melden(await anhangOeffnen(anhang));
  }

  if (istBild(anhang.typ)) {
    return <Bildvorschau anhang={anhang} eigen={eigen} oeffnen={holen} />;
  }

  return (
    <button type="button" onClick={holen}
            className={`mt-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-start text-xs transition ${eigen
              ? 'bg-white/15 hover:bg-white/25'
              : 'bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-950'}`}>
      <span aria-hidden className="text-base">📄</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{anhang.name}</span>
        <span className={eigen ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}>
          {groesseLesbar(anhang.groesse)} · {t('chat.herunterladenTipp')}
        </span>
      </span>
    </button>
  );
}

/** Bildvorschau. Der Inhalt wird angemeldet geladen und als Blob eingebunden. */
function Bildvorschau({ anhang, eigen, oeffnen }: {
  anhang: Anhang;
  eigen: boolean;
  oeffnen: () => void;
}) {
  const [quelle, setQuelle] = useState<string | null>(null);

  useEffect(() => {
    let verworfen = false;
    let erzeugt: string | null = null;
    void anhangAlsBlobUrl(anhang.id).then(url => {
      if (verworfen) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      erzeugt = url;
      setQuelle(url);
    });
    // Ohne Freigabe hält der Browser jedes angezeigte Bild bis zum Neuladen
    // im Speicher.
    return () => {
      verworfen = true;
      if (erzeugt) URL.revokeObjectURL(erzeugt);
    };
  }, [anhang.id]);

  return (
    <button type="button" onClick={oeffnen} className="mt-1 block max-w-full">
      {quelle ? (
        <img src={quelle} alt={anhang.name}
             className="max-h-56 w-auto rounded-lg border border-black/10" />
      ) : (
        <span className="flex h-24 w-40 items-center justify-center rounded-lg bg-black/10 text-xs">
          Bild wird geladen …
        </span>
      )}
      <span className={`mt-1 block truncate text-start text-[11px] ${eigen ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}>
        {anhang.name} · {groesseLesbar(anhang.groesse)}
      </span>
    </button>
  );
}
